import { useState, useRef, useEffect, type FormEvent } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search as SearchIcon, Music, User, Disc, MapPin } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type ImageProp = { url: string; alt_text: string } | null

type BandResult = { id: number; name: string; slug: string; image: ImageProp }
type PersonResult = { id: number; name: string; slug: string; image: ImageProp }
type RecordingResult = { id: number; title: string; slug: string; recordable_name: string | null; recordable_slug: string | null; image: ImageProp }
type VenueResult = { id: number; name: string; slug: string; city: string | null; state: string | null; image: ImageProp }

type Results = {
  bands: BandResult[]
  people: PersonResult[]
  recordings: RecordingResult[]
  venues: VenueResult[]
}

type Props = {
  query: string | null
  results: Results
}

function ResultThumb({ image, fallbackIcon }: { image: ImageProp; fallbackIcon: React.ReactNode }) {
  if (image) {
    return (
      <img
        src={image.url}
        alt={image.alt_text}
        className="w-10 h-10 rounded-md object-cover shrink-0 bg-stone-100 dark:bg-stone-800"
      />
    )
  }
  return (
    <div className="w-10 h-10 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0 text-stone-400 dark:text-stone-600">
      {fallbackIcon}
    </div>
  )
}

function ResultGroup<T>({
  title,
  items,
  renderItem,
  icon,
}: {
  title: string
  items: T[]
  renderItem: (item: T) => React.ReactNode
  icon: React.ReactNode
}) {
  if (items.length === 0) return null
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-amber-500">{icon}</span>
        <h2 className="text-sm font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wider">{title}</h2>
        <span className="text-xs text-stone-400 dark:text-stone-500">({items.length}{items.length === 10 ? "+" : ""})</span>
      </div>
      <ul className="divide-y divide-stone-100 dark:divide-stone-800 rounded-xl border border-stone-200 dark:border-stone-800 overflow-hidden bg-white dark:bg-stone-900">
        {items.map((item, i) => (
          <li key={i}>{renderItem(item)}</li>
        ))}
      </ul>
    </section>
  )
}

export default function SearchPage({ query, results }: Props) {
  const [input, setInput] = useState(query ?? "")
  const inputRef = useRef<HTMLInputElement>(null)
  const total = results.bands.length + results.people.length + results.recordings.length + results.venues.length
  const hasQuery = (query ?? "").length >= 2

  useEffect(() => {
    if (!hasQuery) inputRef.current?.focus()
  }, [hasQuery])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const q = input.trim()
    if (!q) return
    router.get("/search", { q }, { preserveState: false })
  }

  const description = hasQuery
    ? `Search results for "${query}" — Australian Music Database`
    : "Search bands, people, recordings and venues — Australian Music Database"

  return (
    <PublicShell>
      <Head title={hasQuery ? `"${query}" — Search` : "Search"}>
        <meta name="description" content={description} />
        <meta property="og:title" content={hasQuery ? `"${query}" — Search` : "Search"} />
        <meta property="og:description" content={description} />
        <meta name="robots" content="noindex" />
      </Head>

      <div className="max-w-2xl mx-auto space-y-8">
        {/* Search input */}
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search bands, people, recordings, venues…"
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-base text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 shadow-sm"
            />
          </div>
        </form>

        {/* Results */}
        {hasQuery && (
          <div className="space-y-8">
            {total === 0 ? (
              <div className="text-center py-16 text-stone-400 dark:text-stone-500">
                <SearchIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-base font-medium text-stone-500 dark:text-stone-400">No results for &ldquo;{query}&rdquo;</p>
                <p className="text-sm mt-1">Try a different spelling or a shorter term.</p>
              </div>
            ) : (
              <>
                <ResultGroup
                  title="Bands"
                  items={results.bands}
                  icon={<Music className="w-4 h-4" />}
                  renderItem={(b) => (
                    <Link
                      href={`/bands/${b.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors no-underline group"
                    >
                      <ResultThumb image={b.image} fallbackIcon={<Music className="w-4 h-4" />} />
                      <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {b.name}
                      </span>
                    </Link>
                  )}
                />

                <ResultGroup
                  title="People"
                  items={results.people}
                  icon={<User className="w-4 h-4" />}
                  renderItem={(p) => (
                    <Link
                      href={`/people/${p.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors no-underline group"
                    >
                      <ResultThumb image={p.image} fallbackIcon={<User className="w-4 h-4" />} />
                      <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {p.name}
                      </span>
                    </Link>
                  )}
                />

                <ResultGroup
                  title="Recordings"
                  items={results.recordings}
                  icon={<Disc className="w-4 h-4" />}
                  renderItem={(r) => (
                    <Link
                      href={`/recordings/${r.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors no-underline group"
                    >
                      <ResultThumb image={r.image} fallbackIcon={<Disc className="w-4 h-4" />} />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors block truncate">
                          {r.title}
                        </span>
                        {r.recordable_name && (
                          <span className="text-xs text-stone-400 dark:text-stone-500 truncate block">
                            {r.recordable_name}
                          </span>
                        )}
                      </div>
                    </Link>
                  )}
                />

                <ResultGroup
                  title="Venues"
                  items={results.venues}
                  icon={<MapPin className="w-4 h-4" />}
                  renderItem={(v) => (
                    <Link
                      href={`/venues/${v.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors no-underline group"
                    >
                      <ResultThumb image={v.image} fallbackIcon={<MapPin className="w-4 h-4" />} />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors block truncate">
                          {v.name}
                        </span>
                        {(v.city || v.state) && (
                          <span className="text-xs text-stone-400 dark:text-stone-500 truncate block">
                            {[v.city, v.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                    </Link>
                  )}
                />
              </>
            )}
          </div>
        )}

        {!hasQuery && (
          <div className="text-center py-8 text-stone-400 dark:text-stone-500 text-sm">
            Search across {" "}
            <Link href="/bands" className="text-amber-600 dark:text-amber-400 hover:underline">bands</Link>,{" "}
            <Link href="/people" className="text-amber-600 dark:text-amber-400 hover:underline">people</Link>,{" "}
            <Link href="/recordings" className="text-amber-600 dark:text-amber-400 hover:underline">recordings</Link>, and{" "}
            <Link href="/venues" className="text-amber-600 dark:text-amber-400 hover:underline">venues</Link>.
          </div>
        )}
      </div>
    </PublicShell>
  )
}
